import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { applyAttendanceCalendarJob, fetchMyOrganisationSummary } from "../../../Moduels/attendance/attendanceApi";
import DatePickerField from "../../common/DatePickerField";
import EmployeeMultiSelect from "../../common/EmployeeMultiSelect";
import OrganisationDisplayField from "../../common/OrganisationDisplayField";

export default function ApplyCalendar({ embedded = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organisationId, setOrganisationId] = useState("");
  const [organisationName, setOrganisationName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const org = await fetchMyOrganisationSummary();
        setOrganisationId(org?.id || "");
        setOrganisationName(org?.name || org?.legal_name || "");
      } catch {
        // organisation stays empty until loaded
      }
    })();
  }, []);

  const submit = async () => {
    setError("");
    setResult(null);
    if (!organisationId || !fromDate || !toDate) {
      setError("organisation_id, from_date and to_date are required");
      return;
    }
    setLoading(true);
    try {
      const res = await applyAttendanceCalendarJob({
        organisation_id: organisationId,
        from_date: fromDate,
        to_date: toDate,
        employee_ids: selectedEmployeeIds.length ? selectedEmployeeIds : null,
      });
      setResult(res);
      const next = new URLSearchParams(searchParams);
      next.set("tab", "daily");
      if (fromDate) next.set("month", fromDate.slice(0, 7));
      if (selectedEmployeeIds.length === 1) {
        next.set("employee", selectedEmployeeIds[0]);
      }
      setSearchParams(next, { replace: true });
    } catch (e) {
      setError(e.message || "Failed to apply calendar marks");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? "att-embedded p-4 md:p-6" : "min-h-screen bg-gray-50 p-8"}>
      {!embedded ? (
        <div className="bg-white p-6 rounded-xl shadow-md mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Apply Calendar Marks</h2>
          <p className="text-sm text-gray-500 mt-1">
            For the selected date range: marks each <b>working day</b> as Present (P), skips
            holidays and week-offs (those get <b>H</b> / <b>WO</b> from org policy).
          </p>
        </div>
      ) : null}

      <div className={embedded ? "space-y-4" : "bg-white p-6 rounded-xl shadow-md"}>
        <div className="grid md:grid-cols-2 gap-4">
          <OrganisationDisplayField
            organisationId={organisationId}
            organisationName={organisationName}
            hint="Working days → Present; org holidays → H; configured week-offs → WO."
          />
          <EmployeeMultiSelect
            value={selectedEmployeeIds}
            onChange={setSelectedEmployeeIds}
            label="Employees (optional)"
            hint="No selection = apply to all employees in the organisation"
          />
          <DatePickerField
            label="From date"
            required
            value={fromDate}
            onChange={setFromDate}
            max={toDate || undefined}
          />
          <DatePickerField
            label="To date"
            required
            value={toDate}
            onChange={setToDate}
            min={fromDate || undefined}
          />
        </div>

        <button
          onClick={submit}
          disabled={loading}
          className={`mt-4 px-5 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm ${
            loading ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
          }`}
        >
          {loading ? "Running..." : "Apply Calendar Marks"}
        </button>

        {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}
        {result ? (
          <div className="mt-3 text-sm text-green-700 space-y-1">
            <p>
              Saved {result.processed_rows} row(s) for {result.employees ?? "—"} employee(s):{" "}
              <b>{result.present_rows ?? 0}</b> working-day mark(s) as Present,{" "}
              <b>{result.holiday_rows ?? 0}</b> holiday, <b>{result.week_off_rows ?? 0}</b> week-off.
              {Array.isArray(result.errors) && result.errors.length > 0
                ? ` ${result.errors.length} error(s).`
                : ""}
            </p>
            <p>Opening Daily Attendance calendar…</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
